// CMS-Ready Case Studies Data Structure
// Fields: title, slug, industry, services, location, problem, strategy, results, testimonial, featured

/**
 * P5.5 Evidence lock: ningún caso se publica con nombre de cliente, métricas o
 * testimonio si no hay autorización/evidencia registrada en
 * SEO-CASE-STUDY-PIPELINE.md y SEO-EVIDENCE-REGISTER.md.
 */
export type CaseEvidenceStatus =
  | 'PUBLIC_VERIFIED'
  | 'PUBLIC_ANONYMIZED'
  | 'PRIVATE_ONLY'
  | 'CLIENT_NAME_AUTHORIZATION_REQUIRED';

export interface CaseStudy {
  id: string;
  /** Estado de evidencia; solo PUBLIC_* se renderiza públicamente. */
  evidenceStatus: CaseEvidenceStatus;
  slug: string;
  title: string;
  industry: string;
  industryEn: string;
  services: string[];
  servicesEn: string[];
  location: string;
  featured: boolean;
  
  // Evidence images (screenshots, dashboards, etc.)
  evidenceImages?: {
    src: string;
    alt: string;
    altEn: string;
    caption: string;
    captionEn: string;
  }[];
  
  // Highlight result for Hero
  highlightResult?: {
    before: string;
    beforeEn: string;
    after: string;
    afterEn: string;
    timeline: string;
    timelineEn: string;
  };
  
  // Context
  situation: string;
  situationEn: string;
  problems: string[];
  problemsEn: string[];
  limitations: string[];
  limitationsEn: string[];
  
  // Objectives
  objectives: string[];
  objectivesEn: string[];
  kpis: string[];
  kpisEn: string[];
  
  // Strategy
  strategy: {
    title: string;
    titleEn: string;
    description: string;
    descriptionEn: string;
  }[];
  
  // Implementation
  steps: {
    title: string;
    titleEn: string;
    description: string;
    descriptionEn: string;
  }[];
  
  // Results
  results: {
    metric: string;
    metricEn: string;
    description: string;
    descriptionEn: string;
  }[];
  resultNote: string;
  resultNoteEn: string;
  
  // Learnings
  learnings: string[];
  learningsEn: string[];
  
  // Testimonial
  testimonial?: {
    quote: string;
    quoteEn: string;
    author: string;
    role: string;
    roleEn: string;
  };
}

export const caseStudies: CaseStudy[] = [
  {
    id: "1",
    evidenceStatus: 'PUBLIC_ANONYMIZED',
    slug: "marca-abrasivos-amazon-usa",
    title: "Marca Mexicana de Abrasivos - Amazon USA e Internacional",
    industry: "Abrasivos industriales (lijas y discos)",
    industryEn: "Industrial Abrasives (sandpaper and discs)",
    services: [
      "Amazon SEO",
      "Análisis de categoría",
      "Optimización de listings",
      "Brand Registry + A+",
      "Amazon Ads",
      "Expansión internacional"
    ],
    servicesEn: [
      "Amazon SEO",
      "Category analysis",
      "Listing optimization",
      "Brand Registry + A+",
      "Amazon Ads",
      "International expansion"
    ],
    location: "Amazon USA → Europa, Japón",
    featured: true,
    
    // Evidencia visual (screenshots de Amazon Seller Central)
    evidenceImages: [
      {
        src: "/assets/case-studies/amazon-abrasives-products.jpg",
        alt: "Productos de abrasivos industriales - lijas y discos para manufactura",
        altEn: "Industrial abrasive products - sandpaper and discs for manufacturing",
        caption: "Línea de productos: Lijas y discos abrasivos industriales",
        captionEn: "Product line: Industrial sandpaper and abrasive discs"
      },
      {
        src: "/assets/case-studies/amazon-sales-dashboard.png",
        alt: "Amazon Seller Central - Sales Dashboard mostrando crecimiento de ventas",
        altEn: "Amazon Seller Central - Sales Dashboard showing sales growth",
        caption: "Sales Dashboard: $1,220,627 en ventas totales, 93,108 unidades",
        captionEn: "Sales Dashboard: $1,220,627 in total sales, 93,108 units"
      },
      {
        src: "/assets/case-studies/amazon-campaigns-roas.png",
        alt: "Amazon Ads Campaigns - ROAS 3.08 y métricas de rendimiento",
        altEn: "Amazon Ads Campaigns - ROAS 3.08 and performance metrics",
        caption: "$631,592 en ventas vía Ads | ROAS 3.08 | ACOS 32.43%",
        captionEn: "$631,592 in Ads sales | ROAS 3.08 | ACOS 32.43%"
      },
      {
        src: "/assets/case-studies/amazon-international-sales.png",
        alt: "Ventas internacionales: UK, Alemania, Francia, España, Japón",
        altEn: "International sales: UK, Germany, France, Spain, Japan",
        caption: "Expansión: $28,441 Europa + $80 Japón en primeros meses",
        captionEn: "Expansion: $28,441 Europe + $80 Japan in first months"
      }
    ],
    
    // Resultado destacado para Hero
    highlightResult: {
      before: "$800 USD/mes",
      beforeEn: "$800 USD/mo",
      after: "+$79,000 USD/mes",
      afterEn: "+$79,000 USD/mo",
      timeline: "10 meses",
      timelineEn: "10 months"
    },
    
    situation: "Una marca mexicana de lijas y abrasivos industriales, con un producto sólido y experiencia en su mercado tradicional, enfrentaba una situación clara en Amazon USA: ventas muy bajas durante un periodo prolongado y Amazon no representaba un canal estratégico real. El problema no era el producto, sino la estrategia dentro de Amazon.",
    situationEn: "A Mexican sandpaper and industrial abrasives brand, with a solid product and experience in their traditional market, faced a clear situation on Amazon USA: very low sales for a prolonged period and Amazon was not a real strategic channel. The problem wasn't the product, but the strategy within Amazon.",
    
    problems: [
      "Ventas muy bajas durante un periodo prolongado",
      "Amazon no representaba un canal estratégico real",
      "Listings sin estructura clara de SEO interno",
      "Falta de diferenciación en una categoría altamente competitiva",
      "Inversión publicitaria sin una base sólida de investigación",
      "Bajo aprovechamiento de herramientas avanzadas del marketplace"
    ],
    problemsEn: [
      "Very low sales for a prolonged period",
      "Amazon was not a real strategic channel",
      "Listings without clear internal SEO structure",
      "Lack of differentiation in a highly competitive category",
      "Advertising investment without a solid research foundation",
      "Underutilization of advanced marketplace tools"
    ],
    
    limitations: [
      "Categoría de abrasivos altamente saturada",
      "Competencia de marcas estadounidenses establecidas",
      "Necesidad de adaptación para mercados internacionales"
    ],
    limitationsEn: [
      "Highly saturated abrasives category",
      "Competition from established American brands",
      "Need for adaptation for international markets"
    ],
    
    objectives: [
      "Transformar Amazon USA en un canal rentable y escalable",
      "Identificar oportunidades reales dentro de la categoría de abrasivos",
      "Construir listings optimizados para búsqueda + conversión",
      "Desbloquear activos avanzados mediante Brand Registry",
      "Escalar ventas sin depender exclusivamente de publicidad",
      "Preparar el terreno para expansión internacional"
    ],
    objectivesEn: [
      "Transform Amazon USA into a profitable and scalable channel",
      "Identify real opportunities within the abrasives category",
      "Build listings optimized for search + conversion",
      "Unlock advanced assets through Brand Registry",
      "Scale sales without depending exclusively on advertising",
      "Prepare the ground for international expansion"
    ],
    
    kpis: [
      "Ventas mensuales en Amazon USA",
      "ROAS de campañas publicitarias",
      "Posicionamiento orgánico por keywords",
      "Ventas en marketplaces internacionales"
    ],
    kpisEn: [
      "Monthly sales on Amazon USA",
      "Advertising campaign ROAS",
      "Organic positioning by keywords",
      "Sales in international marketplaces"
    ],
    
    strategy: [
      {
        title: "1️⃣ Análisis de Productos y Categorías",
        titleEn: "1️⃣ Product and Category Analysis",
        description: "Revisión profunda del ecosistema de abrasivos en Amazon USA: lijas, discos abrasivos, aplicaciones industriales. Identificación de subcategorías con demanda sostenida y menor saturación. Análisis de productos top considerando formatos, usos por material, claridad del grano, diseño de empaque y estrategia de bundles.",
        descriptionEn: "Deep review of the abrasives ecosystem on Amazon USA: sandpaper, abrasive discs, industrial applications. Identification of subcategories with sustained demand and lower saturation. Analysis of top products considering formats, uses by material, grit clarity, packaging design and bundle strategy."
      },
      {
        title: "2️⃣ Investigación de Keywords para Amazon SEO",
        titleEn: "2️⃣ Keyword Research for Amazon SEO",
        description: "Para cada keyword relevante: búsquedas exactas (últimos 30 días), volumen amplio, tendencias 30 vs 30 y 90 vs 90 días, facilidad de posicionamiento, número de productos orgánicos/patrocinados, costo estimado de PPC (concordancia exacta y amplia). Priorización de keywords con intención de compra real, no solo volumen.",
        descriptionEn: "For each relevant keyword: exact searches (last 30 days), broad volume, 30 vs 30 and 90 vs 90 day trends, positioning ease, number of organic/sponsored products, estimated PPC cost (exact and broad match). Prioritization of keywords with real purchase intent, not just volume."
      },
      {
        title: "3️⃣ Brand Registry + Activos Premium",
        titleEn: "3️⃣ Brand Registry + Premium Assets",
        description: "Registro de marca dentro de Amazon para acceso a: Contenido A+, Video en listings, Protección de marca, Programa Amazon Vine para reviews iniciales.",
        descriptionEn: "Brand registration within Amazon for access to: A+ Content, Video in listings, Brand Protection, Amazon Vine program for initial reviews."
      },
      {
        title: "4️⃣ Amazon Ads Alineado a SEO",
        titleEn: "4️⃣ Amazon Ads Aligned to SEO",
        description: "Campañas estructuradas por tipo de producto, uso/aplicación e intención de búsqueda. Optimización continua basada en datos reales, no suposiciones.",
        descriptionEn: "Campaigns structured by product type, use/application and search intent. Continuous optimization based on real data, not assumptions."
      }
    ],
    
    steps: [
      {
        title: "Optimización y creación de listings",
        titleEn: "Listing optimization and creation",
        description: "Títulos optimizados por intención + aplicación industrial. Bullets orientados a uso real y compatibilidad. Imágenes rediseñadas con enfoque técnico y claridad visual. Backend terms alineados a sinónimos y variaciones reales.",
        descriptionEn: "Titles optimized by intent + industrial application. Bullets oriented to real use and compatibility. Redesigned images with technical focus and visual clarity. Backend terms aligned to real synonyms and variations."
      },
      {
        title: "Registro y activación Brand Registry",
        titleEn: "Brand Registry registration and activation",
        description: "Completar registro de marca en Amazon. Activación de Contenido A+ en todos los listings principales. Integración de video de producto. Solicitud de programa Vine para reviews auténticas.",
        descriptionEn: "Complete brand registration on Amazon. A+ Content activation on all main listings. Product video integration. Vine program request for authentic reviews."
      },
      {
        title: "Lanzamiento de campañas Amazon Ads",
        titleEn: "Amazon Ads campaign launch",
        description: "Campañas Sponsored Products por categoría. Campañas Sponsored Brands para awareness. Estructura de pujas basada en investigación de keywords. A/B testing continuo de creativos y segmentación.",
        descriptionEn: "Sponsored Products campaigns by category. Sponsored Brands campaigns for awareness. Bid structure based on keyword research. Continuous A/B testing of creatives and targeting."
      },
      {
        title: "Expansión internacional",
        titleEn: "International expansion",
        description: "Replicación del modelo exitoso en: Reino Unido, Europa (Alemania, Francia, España, Italia, Países Bajos), Japón. Adaptación de keywords, listings y estructura por marketplace.",
        descriptionEn: "Replication of the successful model in: United Kingdom, Europe (Germany, France, Spain, Italy, Netherlands), Japan. Adaptation of keywords, listings and structure by marketplace."
      }
    ],
    
    results: [
      {
        metric: "Ventas mensuales",
        metricEn: "Monthly sales",
        description: "De $800 USD/mes a +$79,000 USD/mes en Amazon USA",
        descriptionEn: "From $800 USD/mo to +$79,000 USD/mo on Amazon USA"
      },
      {
        metric: "Ventas vía Amazon Ads",
        metricEn: "Amazon Ads sales",
        description: "Más de $631,000 USD generados",
        descriptionEn: "Over $631,000 USD generated"
      },
      {
        metric: "Órdenes procesadas",
        metricEn: "Orders processed",
        description: "44,727 órdenes en el período",
        descriptionEn: "44,727 orders in the period"
      },
      {
        metric: "ROAS promedio",
        metricEn: "Average ROAS",
        description: "3.08 de retorno sobre inversión publicitaria",
        descriptionEn: "3.08 return on advertising spend"
      },
      {
        metric: "ACOS promedio",
        metricEn: "Average ACOS",
        description: "32.43% - eficiencia publicitaria sostenible",
        descriptionEn: "32.43% - sustainable advertising efficiency"
      },
      {
        metric: "Expansión global",
        metricEn: "Global expansion",
        description: "Apertura en UK, Europa y Japón",
        descriptionEn: "Opening in UK, Europe and Japan"
      }
    ],
    resultNote: "Datos reales respaldados por reportes internos de Amazon Seller Central. Período aproximado: 10 meses. Marca y datos sensibles presentados de forma confidencial.",
    resultNoteEn: "Real data backed by internal Amazon Seller Central reports. Approximate period: 10 months. Brand and sensitive data presented confidentially.",
    
    learnings: [
      "En categorías técnicas, Amazon se gana con investigación, no con intuición.",
      "La claridad visual y técnica del listing impacta directamente en la conversión.",
      "Publicidad sin estructura de keywords suele escalar costos, no ventas.",
      "Brand Registry desbloquea ventajas competitivas reales.",
      "Amazon puede convertirse en un canal internacional si se estructura correctamente desde el inicio."
    ],
    learningsEn: [
      "In technical categories, Amazon is won with research, not intuition.",
      "Visual and technical clarity of the listing directly impacts conversion.",
      "Advertising without keyword structure usually scales costs, not sales.",
      "Brand Registry unlocks real competitive advantages.",
      "Amazon can become an international channel if structured correctly from the start."
    ],
    
    testimonial: {
      quote: "Amazon pasó de ser un canal secundario a convertirse en uno de los más importantes de nuestro negocio. La estrategia de investigación fue la diferencia.",
      quoteEn: "Amazon went from being a secondary channel to becoming one of the most important in our business. The research strategy made the difference.",
      author: "Director Comercial",
      role: "Marca de Abrasivos Industriales",
      roleEn: "Industrial Abrasives Brand"
    }
  },
  {
    id: "2",
    evidenceStatus: 'CLIENT_NAME_AUTHORIZATION_REQUIRED',
    slug: "el-nino-aguas-frescas-branding",
    title: "El Niño Aguas Frescas × Sunny Sky Products",
    industry: "Bebidas / Consumo masivo",
    industryEn: "Beverages / Consumer Goods",
    services: [
      "Estrategia de branding cultural",
      "Rediseño de identidad visual",
      "Storytelling visual",
      "Sistema gráfico multicanal"
    ],
    servicesEn: [
      "Cultural branding strategy",
      "Visual identity redesign",
      "Visual storytelling",
      "Multichannel graphic system"
    ],
    location: "Houston, Texas, EE. UU.",
    featured: true,
    
    // Evidence images
    evidenceImages: [
      {
        src: "/assets/case-studies/el-nino-beverages-display.jpg",
        alt: "Aguas frescas mexicanas tradicionales en dispensadores de vidrio",
        altEn: "Traditional Mexican aguas frescas in glass dispensers",
        caption: "Producto: Aguas frescas tradicionales - Jamaica, Horchata, Tamarindo",
        captionEn: "Product: Traditional fresh waters - Jamaica, Horchata, Tamarindo"
      },
      {
        src: "/assets/case-studies/el-nino-original.gif",
        alt: "El Niño Aguas Frescas - Identidad visual original (antes)",
        altEn: "El Niño Aguas Frescas - Original visual identity (before)",
        caption: "Identidad original: imagen anticuada y difícil de adaptar",
        captionEn: "Original identity: outdated image difficult to adapt"
      },
      {
        src: "/assets/case-studies/el-nino-branding.png",
        alt: "Nueva identidad visual El Niño Aguas Frescas con mascota y frutas",
        altEn: "New visual identity El Niño Aguas Frescas with mascot and fruits",
        caption: "Nueva identidad: storytelling cultural con conexión emocional",
        captionEn: "New identity: cultural storytelling with emotional connection"
      },
      {
        src: "/assets/case-studies/el-nino-2bowl.jpg",
        alt: "Dispensador El Niño Aguas Frescas - Jamaica y Horchata",
        altEn: "El Niño Aguas Frescas Dispenser - Jamaica and Horchata",
        caption: "Aplicación en dispensadores: Jamaica y Horchata",
        captionEn: "Dispenser application: Jamaica and Horchata"
      }
    ],
    
    // Highlight result
    highlightResult: {
      before: "Imagen anticuada",
      beforeEn: "Outdated image",
      after: "Conexión emocional auténtica",
      afterEn: "Authentic emotional connection",
      timeline: "Reposicionamiento estratégico",
      timelineEn: "Strategic repositioning"
    },
    
    situation: "Sunny Sky Products es una compañía estadounidense especializada en bebidas servidas (dispensed beverages) a nivel internacional, con sede en Houston, Texas. Como parte de su estrategia de crecimiento en el mercado hispano de EE. UU., adquirió la marca El Niño Aguas Frescas, una línea inspirada en sabores tradicionales latinoamericanos.",
    situationEn: "Sunny Sky Products is an American company specializing in dispensed beverages internationally, headquartered in Houston, Texas. As part of their growth strategy in the US Hispanic market, they acquired the El Niño Aguas Frescas brand, a line inspired by traditional Latin American flavors.",
    
    problems: [
      "Imagen anticuada y poco atractiva",
      "Diseño confuso y difícil de adaptar a nuevos formatos",
      "Enfoque excesivamente local, sin alcance multicultural",
      "Falta de conexión emocional con un público hispano diverso",
      "La marca no lograba comunicar autenticidad, frescura ni nostalgia"
    ],
    problemsEn: [
      "Outdated and unappealing image",
      "Confusing design difficult to adapt to new formats",
      "Excessively local focus, without multicultural reach",
      "Lack of emotional connection with a diverse Hispanic audience",
      "The brand failed to communicate authenticity, freshness or nostalgia"
    ],
    
    limitations: [
      "Necesidad de conectar con hispanos de diversos países de origen",
      "No podía depender del idioma ni de referencias regionales específicas",
      "Competencia establecida en el mercado de aguas frescas en EE. UU."
    ],
    limitationsEn: [
      "Need to connect with Hispanics from various countries of origin",
      "Could not depend on language or specific regional references",
      "Established competition in the US aguas frescas market"
    ],
    
    objectives: [
      "Reconstruir la identidad emocional de la marca",
      "Conectar con el consumidor hispano a través de símbolos culturales universales",
      "Crear una imagen moderna, cálida y escalable",
      "Permitir que la marca funcionara en distintos canales y puntos de contacto",
      "Apoyar el reposicionamiento comercial en EE. UU."
    ],
    objectivesEn: [
      "Rebuild the brand's emotional identity",
      "Connect with Hispanic consumers through universal cultural symbols",
      "Create a modern, warm and scalable image",
      "Allow the brand to work across different channels and touchpoints",
      "Support commercial repositioning in the US"
    ],
    
    kpis: [
      "Coherencia visual en punto de venta",
      "Conexión emocional con consumidor hispano",
      "Escalabilidad del sistema gráfico",
      "Reconocimiento de marca"
    ],
    kpisEn: [
      "Visual coherence at point of sale",
      "Emotional connection with Hispanic consumer",
      "Graphic system scalability",
      "Brand recognition"
    ],
    
    strategy: [
      {
        title: "Storytelling Cultural",
        titleEn: "Cultural Storytelling",
        description: "Desarrollo de identidad basada en un principio clave: Las aguas frescas no son solo una bebida; son un recuerdo compartido en la infancia latinoamericana. El nuevo logotipo presenta a un niño sosteniendo un vaso de agua fresca, evocando juegos en la calle, veranos calurosos y tradiciones familiares.",
        descriptionEn: "Identity development based on a key principle: Aguas frescas are not just a drink; they are a shared childhood memory in Latin America. The new logo features a boy holding a glass of agua fresca, evoking street games, hot summers and family traditions."
      },
      {
        title: "Símbolo Culturalmente Inclusivo",
        titleEn: "Culturally Inclusive Symbol",
        description: "El diseño fue creado para ser culturalmente inclusivo, conectando con consumidores hispanos sin importar su país de origen. Símbolos universales de la infancia latinoamericana que trascienden fronteras específicas.",
        descriptionEn: "The design was created to be culturally inclusive, connecting with Hispanic consumers regardless of their country of origin. Universal symbols of Latin American childhood that transcend specific borders."
      },
      {
        title: "Sistema Visual Multicanal",
        titleEn: "Multichannel Visual System",
        description: "Desarrollo de un sistema gráfico adaptable a distintos formatos: etiquetas, botellas, despachadores de bebida, camiones de distribución, uniformes, material promocional, punto de venta, exhibidores y redes sociales.",
        descriptionEn: "Development of a graphic system adaptable to different formats: labels, bottles, beverage dispensers, distribution trucks, uniforms, promotional material, point of sale, displays and social media."
      },
      {
        title: "Universo Visual Coherente",
        titleEn: "Coherent Visual Universe",
        description: "Construcción de un universo visual escalable en el tiempo, pensado para crecer con la marca y mantener consistencia en múltiples canales de comunicación.",
        descriptionEn: "Construction of a scalable visual universe over time, designed to grow with the brand and maintain consistency across multiple communication channels."
      }
    ],
    
    steps: [
      {
        title: "Investigación cultural",
        titleEn: "Cultural research",
        description: "Análisis de símbolos, memorias y experiencias compartidas en la infancia latinoamericana que conectan con la experiencia de las aguas frescas.",
        descriptionEn: "Analysis of symbols, memories and shared experiences in Latin American childhood that connect with the aguas frescas experience."
      },
      {
        title: "Rediseño del logotipo",
        titleEn: "Logo redesign",
        description: "Creación del nuevo logotipo con el niño caricaturizado, enfocado en empatía cultural y conexión emocional inmediata.",
        descriptionEn: "Creation of the new logo with the cartoon boy, focused on cultural empathy and immediate emotional connection."
      },
      {
        title: "Desarrollo de aplicaciones gráficas",
        titleEn: "Graphic applications development",
        description: "Diseño de etiquetas, botellas, despachadores de bebida (2 y 3 bowls), camiones de distribución, uniformes y material promocional.",
        descriptionEn: "Design of labels, bottles, beverage dispensers (2 and 3 bowls), distribution trucks, uniforms and promotional material."
      },
      {
        title: "Sistema para punto de venta",
        titleEn: "Point of sale system",
        description: "Creación de exhibidores, señalización y materiales para tiendas de conveniencia en EE. UU.",
        descriptionEn: "Creation of displays, signage and materials for convenience stores in the US."
      }
    ],
    
    results: [
      {
        metric: "Coherencia de marca",
        metricEn: "Brand coherence",
        description: "Presentación profesional y consistente en todos los canales",
        descriptionEn: "Professional and consistent presentation across all channels"
      },
      {
        metric: "Conexión emocional",
        metricEn: "Emotional connection",
        description: "Mayor identificación del consumidor hispano con la marca",
        descriptionEn: "Greater Hispanic consumer identification with the brand"
      },
      {
        metric: "Identidad alineada",
        metricEn: "Aligned identity",
        description: "Valores de autenticidad, nostalgia y alegría comunicados visualmente",
        descriptionEn: "Values of authenticity, nostalgia and joy communicated visually"
      },
      {
        metric: "Base escalable",
        metricEn: "Scalable foundation",
        description: "Sistema preparado para expansión en tiendas de conveniencia en EE. UU.",
        descriptionEn: "System prepared for expansion in US convenience stores"
      }
    ],
    resultNote: "El rediseño permitió que El Niño Aguas Frescas hablara al corazón de su audiencia, más allá del idioma o del país de origen. Marca y activos desarrollados como parte de una estrategia de expansión al mercado hispano.",
    resultNoteEn: "The redesign allowed El Niño Aguas Frescas to speak to the heart of its audience, beyond language or country of origin. Brand and assets developed as part of an expansion strategy to the Hispanic market.",
    
    learnings: [
      "En mercados multiculturales, el diseño visual es un lenguaje universal.",
      "El branding emocional puede ser más poderoso que el mensaje racional.",
      "Los símbolos culturales compartidos generan conexión inmediata.",
      "Una identidad bien construida facilita la expansión comercial.",
      "El diseño estratégico puede transformar un producto en una experiencia cultural."
    ],
    learningsEn: [
      "In multicultural markets, visual design is a universal language.",
      "Emotional branding can be more powerful than rational messaging.",
      "Shared cultural symbols generate immediate connection.",
      "A well-built identity facilitates commercial expansion.",
      "Strategic design can transform a product into a cultural experience."
    ],
    
    testimonial: {
      quote: "Necesitábamos una marca que conectara con el mercado hispano sin depender del idioma. El nuevo diseño de El Niño logró exactamente eso: una identidad que habla al corazón.",
      quoteEn: "We needed a brand that connected with the Hispanic market without depending on language. The new El Niño design achieved exactly that: an identity that speaks to the heart.",
      author: "Equipo de Marketing",
      role: "Sunny Sky Products",
      roleEn: "Sunny Sky Products"
    }
  },
  {
    id: "3",
    evidenceStatus: 'PUBLIC_VERIFIED',
    slug: "el-venado-foods-branding-ecommerce",
    title: "El Venado Foods - Marca, E-commerce y Posicionamiento",
    industry: "Alimentos / Especias y productos alimenticios",
    industryEn: "Food / Spices and food products",
    services: [
      "Desarrollo de marca",
      "Diseño de logotipos y etiquetas",
      "Desarrollo de e-commerce",
      "SEO e investigación de keywords",
      "SEO local",
      "Pasarela de pago",
      "Material POP"
    ],
    servicesEn: [
      "Brand development",
      "Logo and label design",
      "E-commerce development",
      "SEO and keyword research",
      "Local SEO",
      "Payment gateway",
      "POP material"
    ],
    location: "Houston, Texas, EE. UU.",
    featured: true,
    
    // Evidence images
    evidenceImages: [
      {
        src: "/assets/case-studies/venado-grocery-shelf.jpg",
        alt: "Productos alimenticios hispanos en estante de supermercado",
        altEn: "Hispanic food products on supermarket shelf",
        caption: "Mercado objetivo: Productos hispanos en autoservicios de Texas",
        captionEn: "Target market: Hispanic products in Texas retail stores"
      },
      {
        src: "/assets/case-studies/venado-original.png",
        alt: "El Venado Foods - Identidad visual original (antes del rediseño)",
        altEn: "El Venado Foods - Original visual identity (before redesign)",
        caption: "Identidad original: imagen anticuada sin coherencia visual",
        captionEn: "Original identity: outdated image without visual coherence"
      },
      {
        src: "/assets/case-studies/venado-nuevo.png",
        alt: "Nueva línea de productos El Venado Foods con identidad renovada",
        altEn: "New El Venado Foods product line with renewed identity",
        caption: "Nueva identidad: venado realista, coherencia en productos",
        captionEn: "New identity: realistic deer, product coherence"
      },
      {
        src: "/assets/case-studies/venado-ecommerce.png",
        alt: "E-commerce venadofoods.com con carrito y pasarela de pago",
        altEn: "E-commerce venadofoods.com with cart and payment gateway",
        caption: "Plataforma digital: e-commerce funcional con SEO local",
        captionEn: "Digital platform: functional e-commerce with local SEO"
      }
    ],
    
    // Highlight result
    highlightResult: {
      before: "Sin identidad consolidada",
      beforeEn: "No consolidated identity",
      after: "Marca escalable + E-commerce",
      afterEn: "Scalable brand + E-commerce",
      timeline: "Estrategia integral",
      timelineEn: "Comprehensive strategy"
    },
    
    situation: "El Venado Foods es una empresa empacadora y distribuidora de especias y productos alimenticios con operaciones en Houston, Texas. La compañía atiende tanto el canal mayoreo como autoservicio, con fuerte presencia en el mercado hispano. El cliente buscaba apoyo integral para el lanzamiento de un nuevo producto, así como para fortalecer su identidad corporativa y su presencia digital.",
    situationEn: "El Venado Foods is a spice and food product packaging and distribution company with operations in Houston, Texas. The company serves both wholesale and retail channels, with a strong presence in the Hispanic market. The client sought comprehensive support for launching a new product, as well as strengthening their corporate identity and digital presence.",
    
    problems: [
      "Lanzamiento de nuevo producto (Doña Chela) sin identidad definida",
      "Imagen visual anticuada y mejorable",
      "Falta de coherencia entre productos, empaques y comunicación",
      "Ausencia de plataforma digital estructurada (e-commerce + SEO)",
      "Necesidad de diferenciación en punto de venta para mercado hispano"
    ],
    problemsEn: [
      "New product launch (Doña Chela) without defined identity",
      "Outdated and improvable visual image",
      "Lack of coherence between products, packaging and communication",
      "Absence of structured digital platform (e-commerce + SEO)",
      "Need for differentiation at point of sale for Hispanic market"
    ],
    
    limitations: [
      "El público objetivo compra principalmente en autoservicios (La Michoacana Meat Market, 140+ sucursales)",
      "Condición del cliente: venado debe ser realista, conservar color amarillo",
      "Requería estrategia integral, no solo diseño gráfico aislado"
    ],
    limitationsEn: [
      "Target audience mainly shops at retail stores (La Michoacana Meat Market, 140+ locations)",
      "Client condition: deer must be realistic, keep yellow color",
      "Required comprehensive strategy, not just isolated graphic design"
    ],
    
    objectives: [
      "Diseñar etiqueta y logotipo para nuevo producto (Doña Chela)",
      "Crear identidad visual alineada con mercado hispano en Texas",
      "Fortalecer marca El Venado Foods manteniendo elementos clave",
      "Desarrollar sitio web con capacidades de e-commerce",
      "Preparar marca para ventas directas, mayoreo y posicionamiento local"
    ],
    objectivesEn: [
      "Design label and logo for new product (Doña Chela)",
      "Create visual identity aligned with Hispanic market in Texas",
      "Strengthen El Venado Foods brand while maintaining key elements",
      "Develop website with e-commerce capabilities",
      "Prepare brand for direct sales, wholesale and local positioning"
    ],
    
    kpis: [
      "Coherencia visual en punto de venta",
      "Funcionalidad del e-commerce",
      "Posicionamiento SEO local en Houston",
      "Reconocimiento de marca en autoservicios"
    ],
    kpisEn: [
      "Visual coherence at point of sale",
      "E-commerce functionality",
      "Local SEO positioning in Houston",
      "Brand recognition in retail stores"
    ],
    
    strategy: [
      {
        title: "1️⃣ Investigación de mercado y consumidor",
        titleEn: "1️⃣ Market and consumer research",
        description: "Reuniones estratégicas para entender mercado meta, puntos de venta, canales de distribución, hábitos de compra del consumidor hispano y referencias culturales relevantes. Público principal: consumidor hispano en La Michoacana Meat Market (140+ sucursales en Texas).",
        descriptionEn: "Strategic meetings to understand target market, points of sale, distribution channels, Hispanic consumer buying habits and relevant cultural references. Main audience: Hispanic consumer at La Michoacana Meat Market (140+ locations in Texas)."
      },
      {
        title: "2️⃣ Desarrollo de marca Doña Chela",
        titleEn: "2️⃣ Doña Chela brand development",
        description: "Diseño de logotipo inspirado en vendedoras tradicionales de micheladas en mercados de México. Personajes cercanos, reconocibles y auténticos que generan confianza, familiaridad y conexión cultural inmediata. Identidad pensada para autoservicio y mayoreo.",
        descriptionEn: "Logo design inspired by traditional michelada sellers in Mexican markets. Close, recognizable and authentic characters that generate trust, familiarity and immediate cultural connection. Identity designed for retail and wholesale."
      },
      {
        title: "3️⃣ Rediseño de marca El Venado Foods",
        titleEn: "3️⃣ El Venado Foods brand redesign",
        description: "Nueva representación del venado como símbolo de fuerza, juventud, vitalidad y solidez. Venado realista (no ilustrado), conservando el amarillo como elemento identitario. Integración en empaques, etiquetas, papelería, uniformes y flotilla de transporte.",
        descriptionEn: "New representation of the deer as a symbol of strength, youth, vitality and solidity. Realistic deer (not illustrated), keeping yellow as identity element. Integration in packaging, labels, stationery, uniforms and transport fleet."
      },
      {
        title: "4️⃣ Plataforma digital y SEO",
        titleEn: "4️⃣ Digital platform and SEO",
        description: "Compra de dominio, desarrollo de venadofoods.com, implementación de e-commerce, integración de pasarela de pago, investigación de palabras clave para SEO y contenidos, optimización de SEO local para Houston, Texas.",
        descriptionEn: "Domain purchase, development of venadofoods.com, e-commerce implementation, payment gateway integration, keyword research for SEO and content, local SEO optimization for Houston, Texas."
      }
    ],
    
    steps: [
      {
        title: "Investigación cultural y de mercado",
        titleEn: "Cultural and market research",
        description: "Análisis del consumidor hispano en Texas, canales de distribución, competencia en autoservicios y referencias culturales para el diseño.",
        descriptionEn: "Analysis of Hispanic consumer in Texas, distribution channels, competition in retail stores and cultural references for design."
      },
      {
        title: "Desarrollo de marca Doña Chela",
        titleEn: "Doña Chela brand development",
        description: "Creación de logotipo, etiqueta y materiales POP (posters, displays, danglers) para el nuevo producto de micheladas.",
        descriptionEn: "Creation of logo, label and POP materials (posters, displays, danglers) for the new michelada product."
      },
      {
        title: "Rediseño corporativo El Venado",
        titleEn: "El Venado corporate redesign",
        description: "Nuevo logotipo con venado realista, actualización de empaques, etiquetas, papelería corporativa, uniformes y flotilla de transporte.",
        descriptionEn: "New logo with realistic deer, packaging update, labels, corporate stationery, uniforms and transport fleet."
      },
      {
        title: "Desarrollo web y e-commerce",
        titleEn: "Web and e-commerce development",
        description: "Creación de venadofoods.com con catálogo de productos, carrito de compras, pasarela de pago, SEO on-page y optimización para SEO local en Houston.",
        descriptionEn: "Creation of venadofoods.com with product catalog, shopping cart, payment gateway, on-page SEO and optimization for local SEO in Houston."
      }
    ],
    
    results: [
      {
        metric: "Lanzamiento de producto",
        metricEn: "Product launch",
        description: "Doña Chela lanzada exitosamente con identidad propia",
        descriptionEn: "Doña Chela successfully launched with its own identity"
      },
      {
        metric: "Marca fortalecida",
        metricEn: "Strengthened brand",
        description: "El Venado Foods modernizada y profesionalizada",
        descriptionEn: "El Venado Foods modernized and professionalized"
      },
      {
        metric: "Coherencia visual",
        metricEn: "Visual coherence",
        description: "Identidad unificada en todos los puntos de contacto",
        descriptionEn: "Unified identity across all touchpoints"
      },
      {
        metric: "Presencia digital",
        metricEn: "Digital presence",
        description: "E-commerce funcional con capacidad de venta directa",
        descriptionEn: "Functional e-commerce with direct sales capability"
      },
      {
        metric: "Posicionamiento local",
        metricEn: "Local positioning",
        description: "Mejor visibilidad para mercado hispano en Texas",
        descriptionEn: "Better visibility for Hispanic market in Texas"
      }
    ],
    resultNote: "El trabajo permitió que El Venado Foods contara con una marca preparada para competir y escalar en canales físicos y digitales. Sitio web: venadofoods.com",
    resultNoteEn: "The work allowed El Venado Foods to have a brand ready to compete and scale in physical and digital channels. Website: venadofoods.com",
    
    learnings: [
      "El branding cultural requiere investigación, no estereotipos.",
      "El punto de venta define muchas decisiones de diseño.",
      "La coherencia visual fortalece la confianza del consumidor.",
      "Un e-commerce sin estrategia de marca pierde impacto.",
      "El mercado hispano responde mejor a marcas auténticas y cercanas."
    ],
    learningsEn: [
      "Cultural branding requires research, not stereotypes.",
      "The point of sale defines many design decisions.",
      "Visual coherence strengthens consumer trust.",
      "An e-commerce without brand strategy loses impact.",
      "The Hispanic market responds better to authentic and close brands."
    ],
    
    testimonial: {
      quote: "Queríamos una marca que conectara con nuestros clientes hispanos y una plataforma digital que nos permitiera crecer. El resultado superó nuestras expectativas.",
      quoteEn: "We wanted a brand that connected with our Hispanic customers and a digital platform that would allow us to grow. The result exceeded our expectations.",
      author: "Propietario",
      role: "El Venado Foods",
      roleEn: "El Venado Foods"
    }
  },
 
  {
    id: "6",
    evidenceStatus: 'CLIENT_NAME_AUTHORIZATION_REQUIRED',
    slug: "bristol-myers-squibb-sicodi",
    title: "Bristol Myers Squibb - SICODI",
    industry: "Farmacéutica / Regulatorio",
    industryEn: "Pharmaceutical / Regulatory",
    services: [
      "Desarrollo de software a medida",
      "Gestión documental",
      "Flujos regulatorios",
      "Automatización de procesos",
      "Firma digital",
      "Control de impresión"
    ],
    servicesEn: [
      "Custom software development",
      "Document management",
      "Regulatory workflows",
      "Process automation",
      "Digital signatures",
      "Print control"
    ],
    location: "Ciudad de México, México",
    featured: true,
    
    // Evidence images
    evidenceImages: [
      {
        src: "/assets/case-studies/bms-pharma-technology.jpg",
        alt: "Laboratorio farmacéutico moderno con tecnología de compliance",
        altEn: "Modern pharmaceutical laboratory with compliance technology",
        caption: "Entorno: Laboratorio farmacéutico con sistemas digitales regulatorios",
        captionEn: "Environment: Pharmaceutical lab with regulatory digital systems"
      },
      {
        src: "/assets/case-studies/bms-sicodi-products.png",
        alt: "SICODI - Panel de gestión de productos farmacéuticos",
        altEn: "SICODI - Pharmaceutical product management panel",
        caption: "Gestión de productos: estatus, tipo de dibujo, presentaciones",
        captionEn: "Product management: status, drawing type, presentations"
      },
      {
        src: "/assets/case-studies/bms-sicodi-users.png",
        alt: "SICODI - Sistema de usuarios y roles por área",
        altEn: "SICODI - User system and roles by area",
        caption: "Control de usuarios: Regulatorio, QA, QC, Marketing, Producción",
        captionEn: "User control: Regulatory, QA, QC, Marketing, Production"
      },
      {
        src: "/assets/case-studies/bms-avapro-signatures.png",
        alt: "Empaque farmacéutico con firmas digitales de aprobación",
        altEn: "Pharmaceutical packaging with digital approval signatures",
        caption: "Firmas digitales integradas en empaques regulados",
        captionEn: "Digital signatures integrated in regulated packaging"
      }
    ],
    
    // Highlight result
    highlightResult: {
      before: "Procesos físicos",
      beforeEn: "Physical processes",
      after: "100% digital y auditado",
      afterEn: "100% digital and audited",
      timeline: "11 años de operación",
      timelineEn: "11 years of operation"
    },
    
    situation: "Bristol Myers Squibb (BMS) es una de las farmacéuticas globales más reconocidas, con operaciones altamente reguladas en múltiples países. En México, sus plantas ubicadas en Barranca del Muerto y Tlalpan gestionaban diariamente el desarrollo y aprobación de empaques para medicamentos OTC y controlados, cumpliendo con la Norma Oficial Mexicana NOM-072. El proceso tradicional dependía de revisiones físicas, impresión de múltiples versiones y presencia constante de personal externo dentro de las plantas.",
    situationEn: "Bristol Myers Squibb (BMS) is one of the most recognized global pharmaceutical companies, with highly regulated operations in multiple countries. In Mexico, their plants located in Barranca del Muerto and Tlalpan managed daily the development and approval of packaging for OTC and controlled medications, complying with Mexican Official Standard NOM-072. The traditional process depended on physical reviews, printing multiple versions and constant presence of external personnel inside the plants.",
    
    problems: [
      "Presencia diaria de personal externo con equipo e impresoras dentro de áreas sensibles",
      "Retrasos por recolección de firmas físicas entre múltiples departamentos",
      "Reprocesos frecuentes por falta de control de versiones",
      "Riesgos de impresión al trabajar con archivos distintos entre proveedores",
      "Trazabilidad limitada para auditorías regulatorias",
      "Dependencia excesiva de procesos manuales en un entorno altamente regulado"
    ],
    problemsEn: [
      "Daily presence of external personnel with equipment and printers inside sensitive areas",
      "Delays due to collecting physical signatures between multiple departments",
      "Frequent rework due to lack of version control",
      "Printing risks when working with different files between vendors",
      "Limited traceability for regulatory audits",
      "Excessive dependence on manual processes in a highly regulated environment"
    ],
    
    limitations: [
      "Normativa mexicana NOM-072 estricta",
      "Múltiples áreas involucradas (Regulatorio, Legal, Diseño, Proveedores)",
      "Necesidad de trazabilidad completa para auditorías"
    ],
    limitationsEn: [
      "Strict Mexican NOM-072 regulation",
      "Multiple areas involved (Regulatory, Legal, Design, Vendors)",
      "Need for complete traceability for audits"
    ],
    
    objectives: [
      "Digitalizar completamente el flujo de revisión y aprobación de empaques",
      "Cumplir estrictamente con la normativa mexicana aplicable",
      "Garantizar control de versiones, firmas y trazabilidad",
      "Eliminar riesgos asociados a impresión y manejo de archivos",
      "Reducir tiempos de aprobación sin perder control",
      "Crear un sistema escalable y auditable en el largo plazo"
    ],
    objectivesEn: [
      "Completely digitize the packaging review and approval workflow",
      "Strictly comply with applicable Mexican regulations",
      "Guarantee version control, signatures and traceability",
      "Eliminate risks associated with printing and file handling",
      "Reduce approval times without losing control",
      "Create a scalable and auditable system in the long term"
    ],
    
    kpis: [
      "Tiempo de aprobación de empaques",
      "Errores de impresión por versiones incorrectas",
      "Trazabilidad para auditorías",
      "Satisfacción de proveedor preferente"
    ],
    kpisEn: [
      "Packaging approval time",
      "Print errors due to incorrect versions",
      "Traceability for audits",
      "Preferred vendor satisfaction"
    ],
    
    strategy: [
      {
        title: "1️⃣ Sistema de Control Documental (SICODI)",
        titleEn: "1️⃣ Document Control System (SICODI)",
        description: "Desarrollo de un sistema web a medida para gestión documental y flujos regulatorios, construido desde la lógica del problema, no como software genérico. Registro de usuarios internos y externos con roles y permisos por área.",
        descriptionEn: "Development of a custom web system for document management and regulatory workflows, built from the problem logic, not as generic software. Registration of internal and external users with roles and permissions by area."
      },
      {
        title: "2️⃣ Flujo de Aprobación Digital",
        titleEn: "2️⃣ Digital Approval Workflow",
        description: "Firma digital asignada a cada usuario para validar archivos PDF. Control de versiones con plazos definidos. Aprobación o desaprobación formal con campos obligatorios de retroalimentación en caso de rechazo.",
        descriptionEn: "Digital signature assigned to each user to validate PDF files. Version control with defined deadlines. Formal approval or disapproval with mandatory feedback fields in case of rejection."
      },
      {
        title: "3️⃣ Control de Impresión Seguro",
        titleEn: "3️⃣ Secure Print Control",
        description: "Envío automático del archivo aprobado al buzón de impresión. Notificación directa al proveedor asignado. Restricción de descarga (máximo 3 intentos). Obligación del impresor de emitir certificado de descarga y validar versión correcta.",
        descriptionEn: "Automatic sending of approved file to print queue. Direct notification to assigned vendor. Download restriction (maximum 3 attempts). Printer's obligation to issue download certificate and validate correct version."
      },
      {
        title: "4️⃣ Auditoría y Trazabilidad Total",
        titleEn: "4️⃣ Full Audit and Traceability",
        description: "Bitácoras completas de cada acción, usuario y archivo. Sistema auditable cumpliendo con normativa NOM-072 y protocolos internos de BMS.",
        descriptionEn: "Complete logs of every action, user and file. Auditable system complying with NOM-072 regulation and BMS internal protocols."
      }
    ],
    
    steps: [
      {
        title: "Licitación y presentación técnica",
        titleEn: "Tender and technical presentation",
        description: "Participación en licitación cerrada junto con más de 10 agencias. Presentación técnica frente al comité de decisión de BMS.",
        descriptionEn: "Participation in closed tender along with more than 10 agencies. Technical presentation to BMS decision committee."
      },
      {
        title: "Desarrollo del sistema SICODI",
        titleEn: "SICODI system development",
        description: "Desarrollo completo del sistema web con módulos de usuarios, productos, gestión documental, flujos de aprobación y control de impresión.",
        descriptionEn: "Complete development of web system with user modules, products, document management, approval workflows and print control."
      },
      {
        title: "Adaptación regulatoria",
        titleEn: "Regulatory adaptation",
        description: "Adaptación del sistema a NOM-072 y protocolos internos de BMS. Integración de firma digital y certificados.",
        descriptionEn: "System adaptation to NOM-072 and BMS internal protocols. Digital signature and certificate integration."
      },
      {
        title: "Implementación gradual",
        titleEn: "Gradual implementation",
        description: "Capacitación a equipos internos. Implementación por planta y por tipo de producto. Soporte continuo durante 11 años.",
        descriptionEn: "Training for internal teams. Implementation by plant and product type. Continuous support for 11 years."
      }
    ],
    
    results: [
      {
        metric: "Personal externo",
        metricEn: "External personnel",
        description: "Eliminación total de personal externo dentro de las plantas",
        descriptionEn: "Total elimination of external personnel inside the plants"
      },
      {
        metric: "Tiempos de aprobación",
        metricEn: "Approval times",
        description: "Reducción drástica de los tiempos de aprobación de empaques",
        descriptionEn: "Drastic reduction in packaging approval times"
      },
      {
        metric: "Errores de impresión",
        metricEn: "Print errors",
        description: "Cero errores por uso de archivos incorrectos",
        descriptionEn: "Zero errors due to incorrect file usage"
      },
      {
        metric: "Seguridad documental",
        metricEn: "Document security",
        description: "Mayor seguridad en la gestión de documentación regulada",
        descriptionEn: "Greater security in regulated documentation management"
      },
      {
        metric: "Control de proveedores",
        metricEn: "Vendor control",
        description: "Control estandarizado de proveedores de impresión",
        descriptionEn: "Standardized control of print vendors"
      },
      {
        metric: "Duración del proyecto",
        metricEn: "Project duration",
        description: "11 años de operación continua como proveedor preferente",
        descriptionEn: "11 years of continuous operation as preferred vendor"
      }
    ],
    resultNote: "El sistema operó exitosamente hasta que el portafolio OTC de BMS fue adquirido por Reckitt Benckiser, marcando el cierre natural del proyecto.",
    resultNoteEn: "The system operated successfully until BMS's OTC portfolio was acquired by Reckitt Benckiser, marking the natural closure of the project.",
    
    learnings: [
      "Digitalizar no es 'mover procesos a la nube', sino rediseñar el flujo completo.",
      "En entornos regulados, la trazabilidad es tan importante como la eficiencia.",
      "La seguridad documental debe diseñarse desde el inicio, no añadirse después.",
      "Las soluciones a medida superan al software genérico cuando el riesgo es alto.",
      "La adopción sostenida en el tiempo es la mejor métrica de éxito."
    ],
    learningsEn: [
      "Digitizing is not 'moving processes to the cloud', but redesigning the complete flow.",
      "In regulated environments, traceability is as important as efficiency.",
      "Document security must be designed from the start, not added later.",
      "Custom solutions outperform generic software when risk is high.",
      "Sustained adoption over time is the best metric of success."
    ],
    
    testimonial: {
      quote: "SICODI transformó completamente nuestra operación de empaques. Lo que antes requería días de coordinación física ahora se resuelve digitalmente con trazabilidad total.",
      quoteEn: "SICODI completely transformed our packaging operation. What used to require days of physical coordination is now resolved digitally with full traceability.",
      author: "Gerente de Asuntos Regulatorios",
      role: "Bristol Myers Squibb México",
      roleEn: "Bristol Myers Squibb Mexico"
    }
  }
];

export const getCaseStudyBySlug = (slug: string): CaseStudy | undefined => {
  return caseStudies.find(cs => cs.slug === slug);
};

export const getFeaturedCaseStudies = (): CaseStudy[] => {
  return caseStudies.filter(cs => cs.featured);
};

/** Casos que pueden mostrarse públicamente (P5.5 evidence lock). */
export const publicCaseStudies = caseStudies.filter(
  (c) => c.evidenceStatus === 'PUBLIC_VERIFIED' || c.evidenceStatus === 'PUBLIC_ANONYMIZED'
);

export const isCaseStudyPublic = (c: CaseStudy) =>
  c.evidenceStatus === 'PUBLIC_VERIFIED' || c.evidenceStatus === 'PUBLIC_ANONYMIZED';
